import { StateParams, StateService } from '@uirouter/core';
import { IComponentOptions, IPromise } from 'angular';
import { Transition, TransitionService } from 'angular-ui-router';
import {
    FontFamilyService,
    FontListParams,
    FontListParamsStateParamsResolver,
    FontStyleService,
    IFontFamilyModel,
    IFoundryModel,
    Paginator,
    SortOption,
    debounce,
} from '../../../tn.common';
import { SeriesService } from '../../../tn.fonts/SeriesService';
import { PopoverService } from '../../../tn.popover/service.tn.popover';
import template from './catalog-page.html';

export class CatalogPageController {
    // bindings
    filters: FontListParams = {};
    foundry?: IFoundryModel;

    // locals
    loaded = false;
    families: IFontFamilyModel[] = [];
    fullCatalogueResultsForSearch: IFontFamilyModel[] = [];
    // a lookup so we can get series from the series id.
    series = this.seriesService.listSeries();
    size = 100;
    sizeMin = 50;
    sizeMax = 150;
    sizeStep = 1;
    sortOptions = {
        name: new SortOption('name'),
        released: new SortOption('released', false),
    };
    sort = this.$stateParams.sort;
    customOverrideSize?: number;
    pagination = new Paginator();
    debouncedGetData = debounce(() => this.getData(), 250, false);

    static $inject = [
        '$stateParams',
        '$state',
        '$tnPopover',
        'seriesService',
        'FontFamilyPublicService',
        'FontStylePublicService',
        '$transitions',
    ];
    constructor(
        protected $stateParams: StateParams,
        protected $state: StateService,
        protected $tnPopover: PopoverService,
        protected seriesService: SeriesService,
        public familyService: FontFamilyService,
        public styleService: FontStyleService,
        protected $transitions: TransitionService
    ) {
        this.seriesService._updateSeries().then(() => {
            this.series = this.seriesService.listSeries();
        });

        const onTransitionSuccess = (transition: Transition) => {
            const params = transition.params();
            this.onStateParamsChanged(params);
        };

        // update location when paginator changes page.
        this.pagination.page$.subscribe((page) => {
            // do nothing if the page isn't loaded, otherwise on bootstrap we will set the page to 1.
            // before loading the initial page.
            if (!this.loaded) return;
            // do nothing if the page didn't change.
            if (this.filters.page == page) return;
            this.$state.go('.', { page });
        });

        // the filters parameters are all dynamic to avoid rendering the entire
        // ui. since resolvers are not ran on dynamic transitions, catch the onSuccess
        // and update the filters.
        // we only catch transitions to the same state, since initial state loads should be
        $transitions.onSuccess(
            { to: 'home.fonts.listing' },
            onTransitionSuccess
        );
        $transitions.onSuccess(
            { to: 'home.foundry.fonts.listing' },
            onTransitionSuccess
        );
    }

    $onInit(): void {
        this.onStateParamsChanged(this.$stateParams);
    }

    onStateParamsChanged(params: Partial<StateParams>): void {
        const filters = FontListParamsStateParamsResolver(params);
        this.filters = filters;
        // console.log('CatalogPageController.onStateParamsChanged', {
        //     params,
        //     filters,
        //     pagination: this.pagination,
        // });
        this.$tnPopover.hide();
        this.debouncedGetData();
    }

    getData(): IPromise<void> {
        // local copy of filters in case it changes before callbacks are executed.
        const filters = { ...this.filters };
        this.loaded = false;
        // this is for when this controllers is used on the foundy catalogue.
        // we filter to just that foundry.
        if (this.foundry) {
            filters.foundry = [this.foundry.id];
        }

        return this.familyService
            .list(filters)
            .then((response) => {
                this.pagination.itemCount = response.count;
                this.pagination.perPage = filters.page_size || 20;
                this.pagination.page = filters.page || 1;
                this.families = response.results;

                // if this is a foundry catalogue and there were no results on a name search,
                // then check the full catalogue. if there is a match we'll show a message to
                // the user that there are results for another foundry.
                if (response.count === 0 && this.foundry && filters.search) {
                    return this.familyService
                        .list({ search: filters.search })
                        .then((fullCatResponse) => {
                            this.fullCatalogueResultsForSearch =
                                fullCatResponse.results;
                            this.loaded = true;
                        });
                }
            })
            .finally(() => {
                this.loaded = true;
            });
    }

    clearAllFiltersExceptName(): void {
        this.$state.go('.', {
            name: this.$stateParams.search,
        });
    }
}

export const CatalogPageComponent: IComponentOptions = {
    controller: CatalogPageController,
    controllerAs: 'vm',
    template,
    bindings: {
        filters: '<',
        foundry: '<?',
    },
};
